/**
 * CLE-3071 — revenue-code pricing on the claim modal's Expected Amounts tab.
 *
 * When a line's revenue code has a loaded rate, the backend prices the claim twice (by CPT and by
 * revenue code) and keeps whichever expected is STRICTLY closer to the payer's allowed amount
 * (`chooseExpectedBasis` in clear-api's rev-code-pricing.util.ts). The tab lays out both
 * breakdowns and marks the one the claim's Expected Amount came from.
 */
import { ILineItem } from "../../types/claim";

interface IRevCodeExpectedFields {
  expectedAmount: number | null;
  revCodeExpectedAmount?: number | null;
  revCodeCptExpectedAmount?: number | null;
  category?: string | null;
}

/**
 * Whether the claim's Expected Amount is the revenue-code one.
 *
 * Read from the numbers, not from `category === "REV_CODE_FEE"`: REV_CODE_FEE yields to
 * PRICING_ERROR, the lesser-of categories and EFFECTIVE_YEAR_MISSING, so a claim the revenue code
 * won can carry EFFECTIVE_YEAR_MISSING instead (no contract term covers the date of service). When
 * the revenue code wins, the backend copies its expected into `expectedAmount` unchanged, and it
 * only wins when strictly closer to the allowed amount — so the two expecteds differ. A tie keeps
 * CPT, which is why equal numbers read as "CPT in use".
 */
export const isRevCodeExpectedInUse = (
  claim: IRevCodeExpectedFields
): boolean =>
  claim.revCodeExpectedAmount != null &&
  claim.expectedAmount === claim.revCodeExpectedAmount &&
  claim.revCodeExpectedAmount !== claim.revCodeCptExpectedAmount;

const REVENUE_CODE_PATTERN = /^\d{1,4}$/;

const signatureCodes = (caseSignature: string): Set<string> =>
  new Set(
    caseSignature
      .split(",")
      .map((token) => token.trim().split(":")[0].toUpperCase())
      .filter(Boolean)
  );

/**
 * The claim's lines as the revenue-code pricing saw them: a line whose CPT was swapped out of the
 * revenue-code case signature for its revenue code is shown under that revenue code, so the
 * Claim Allowables table can match it to the revenue-code rate. Every other line is unchanged.
 */
export const revCodeLineItems = (
  lineItems: ILineItem[],
  revCodeCaseSignature: string
): ILineItem[] => {
  const codes = signatureCodes(revCodeCaseSignature);
  return lineItems.map((li) => {
    const cpt = (li.cptCode ?? "").trim().toUpperCase();
    const raw = (li.revenueCode ?? "").trim();
    if (!raw || !REVENUE_CODE_PATTERN.test(raw) || codes.has(cpt)) return li;
    const revenueCode = raw.padStart(4, "0");
    return codes.has(revenueCode) ? { ...li, cptCode: revenueCode } : li;
  });
};
